import React, { useState, useEffect } from 'react';
import { DayPicker } from 'react-day-picker';
import { fr, enUS } from 'date-fns/locale';
import { format, differenceInCalendarDays } from 'date-fns';
import { useTranslation } from 'react-i18next';
import 'react-day-picker/dist/style.css';

/**
 * BookingCalendar — premium inline range picker.
 * - 2 months side-by-side on desktop, 1 month on mobile.
 * - Prevents past dates.
 * - Emits `onChange({ from, to })` and shows a compact summary.
 */
export const BookingCalendar = ({ range, onChange, minDate, blockedDates = [], partialDates = [], days: daysProp }) => {
  const { t, i18n } = useTranslation();
  const dateLocale = (i18n.language || 'fr').toLowerCase().startsWith('en') ? enUS : fr;
  const [monthsToShow, setMonthsToShow] = useState(2);

  useEffect(() => {
    const check = () => setMonthsToShow(window.innerWidth < 768 ? 1 : 2);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  const toDates = (arr) => (arr || []).map((s) => {
    const [y, m, d] = String(s).split('-').map(Number);
    return new Date(y, (m || 1) - 1, d || 1);
  });

  // blockedDates: full-day unavailability (red + non-selectable).
  // partialDates: day partially occupied (orange dot, still selectable).
  const blockedAsDate = React.useMemo(() => toDates(blockedDates), [blockedDates]);
  const partialAsDate = React.useMemo(() => toDates(partialDates), [partialDates]);

  // Compute "today in Bangkok" at midnight for the disabled-before rule.
  const todayBangkok = React.useMemo(() => {
    const nowBK = new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Bangkok' }));
    return new Date(nowBK.getFullYear(), nowBK.getMonth(), nowBK.getDate());
  }, []);

  const disabled = [
    { before: minDate || todayBangkok },
    ...blockedAsDate,
  ];
  // Prefer explicit `days` prop (computed by parent via compute_rental_days
  // logic that mirrors the backend) so the badge stays 100% in sync with the
  // billable duration shown in PriceSummary and the booking total. Fallback
  // to a simple calendar diff when the parent does not pass it (e.g. provider
  // blocked-period picker where hours don't apply).
  const days = daysProp !== undefined
    ? (Number.isFinite(daysProp) && daysProp >= 0 ? daysProp : 0)
    : (range?.from && range?.to ? Math.max(differenceInCalendarDays(range.to, range.from), 1) : 0);

  return (
    <div className="ridalo-calendar" data-testid="booking-calendar">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2 text-sm">
          <div className="flex flex-col">
            <span className="text-[10px] uppercase tracking-widest text-ink-muted">{t('scooters.calendar.departLabel')}</span>
            <span className="font-semibold text-primary" data-testid="calendar-from">
              {range?.from ? format(range.from, 'dd MMM yyyy', { locale: dateLocale }) : t('scooters.calendar.placeholder')}
            </span>
          </div>
          <div className="text-ink-muted mx-2">→</div>
          <div className="flex flex-col">
            <span className="text-[10px] uppercase tracking-widest text-ink-muted">{t('scooters.calendar.returnLabel')}</span>
            <span className="font-semibold text-primary" data-testid="calendar-to">
              {range?.to ? format(range.to, 'dd MMM yyyy', { locale: dateLocale }) : t('scooters.calendar.placeholder')}
            </span>
          </div>
        </div>
        {days > 0 && (
          <span className="inline-flex items-center gap-1 rounded-full bg-accent/10 text-accent px-3 py-1 text-xs font-semibold" data-testid="calendar-days">
            {t('scooters.calendar.daysBadge', { count: days })}
          </span>
        )}
      </div>
      {(blockedAsDate.length > 0 || partialAsDate.length > 0) && (
        <div className="flex flex-wrap items-center gap-3 mb-2 text-[11px] text-ink-muted" data-testid="calendar-legend">
          {blockedAsDate.length > 0 && (
            <span className="inline-flex items-center gap-1.5">
              <span className="w-3 h-3 rounded-full bg-red-200 border border-red-300" />
              {t('scooters.calendar.legendUnavailable')}
            </span>
          )}
          {partialAsDate.length > 0 && (
            <span className="inline-flex items-center gap-1.5">
              <span className="w-3 h-3 rounded-full bg-white ring-2 ring-orange-300" />
              {t('scooters.calendar.legendPartial')}
            </span>
          )}
        </div>
      )}
      <DayPicker
        mode="range"
        selected={range}
        onSelect={onChange}
        numberOfMonths={monthsToShow}
        locale={dateLocale}
        disabled={disabled}
        modifiers={{ unavailable: blockedAsDate, partial: partialAsDate }}
        modifiersClassNames={{
          unavailable: 'bg-red-100 !text-red-500 line-through opacity-70',
          partial: 'ring-2 ring-orange-300 ring-offset-1',
        }}
        showOutsideDays={false}
        classNames={{
          months: 'flex flex-col md:flex-row gap-6',
          month: 'space-y-3',
          caption: 'flex justify-center pt-1 relative items-center',
          caption_label: 'text-base font-semibold text-primary capitalize',
          nav: 'space-x-1 flex items-center',
          nav_button:
            'h-8 w-8 rounded-full border border-slate-200 hover:border-accent hover:bg-accent hover:text-white transition-all bg-transparent flex items-center justify-center',
          nav_button_previous: 'absolute left-1',
          nav_button_next: 'absolute right-1',
          table: 'w-full border-collapse',
          head_row: 'flex',
          head_cell:
            'text-ink-muted rounded-md w-10 max-[374px]:w-auto max-[374px]:flex-1 max-[374px]:min-w-0 max-[374px]:max-w-10 h-8 flex items-center justify-center font-medium text-xs uppercase tracking-wide',
          row: 'flex w-full mt-1',
          cell:
            'relative p-0 text-center max-[374px]:flex-1 max-[374px]:min-w-0 max-[374px]:max-w-[2.75rem] focus-within:relative focus-within:z-20',
          day:
            'h-10 w-10 max-[374px]:h-auto max-[374px]:aspect-square max-[374px]:w-[calc(100%-4px)] max-[374px]:max-w-10 p-0 mx-0.5 rounded-full font-medium text-sm text-primary hover:bg-accent/10 hover:text-accent transition-all aria-selected:opacity-100 focus:outline-none focus:ring-2 focus:ring-accent/40',
          day_range_start:
            '!bg-accent !text-white hover:!bg-accent shadow-md',
          day_range_end:
            '!bg-accent !text-white hover:!bg-accent shadow-md',
          day_range_middle:
            '!bg-accent/15 !text-accent !rounded-none hover:!bg-accent/25',
          day_selected:
            '!bg-accent !text-white',
          day_today: 'ring-2 ring-accent/40',
          day_disabled: 'text-slate-300 hover:!bg-transparent hover:!text-slate-300 cursor-not-allowed',
          day_outside: 'text-slate-300',
          day_hidden: 'invisible',
        }}
      />
    </div>
  );
};
